export interface TranscriptLine {
  speaker: "agent" | "user";
  text: string;
  offsetMs: number;
}

export interface UseCaseEntry {
  kind: "call" | "browser" | "whatsapp";
  /** Badge label — one of the three official industries, or "Quto" (product). */
  industry: string;
  title: string;
  agentName: string;
  description: string;
  /** Where the row's action link points (real route in this project). */
  href: string;
  linkLabel?: string;
  audioSrc?: string;
  videoSrc?: string;
  transcript?: TranscriptLine[];
}

export interface UseCaseStage {
  stage: "Activate" | "Retain" | "Support";
  entries: UseCaseEntry[];
}

const HEALTHCARE_HREF = "/industries/healthcare";
const AUTOMOBILE_HREF = "/industries/automobile";
const REALESTATE_HREF = "/industries/real-estate";
const QUTO_HREF = "/#quto-chatbot";

const INDUSTRY_LINK = "View this industry";
const QUTO_LINK = "See how Quto works";

/**
 * Homepage "Perfect for every use case" data.
 *
 * Exactly the three official industries — Healthcare, Automobile, Real Estate —
 * plus the Quto Chatbot Agent PRODUCT, in every tab. Each stage has a distinct
 * intent: Activate = first contact / lead capture, Retain = follow-up /
 * reminders, Support = questions / assistance. Transcripts are illustrative
 * demos (played by a virtual clock — no fabricated recordings). No real client
 * names are claimed.
 */
export const USE_CASES_DATA: UseCaseStage[] = [
  {
    stage: "Activate",
    entries: [
      {
        kind: "call",
        industry: "Healthcare",
        title: "Appointment booking & scheduling",
        agentName: "Clinic assistant",
        description:
          "Answer patient enquiries, offer available appointment slots, and confirm the booking right on the call.",
        href: HEALTHCARE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi, thanks for calling the clinic. How can I help you today?", offsetMs: 200 },
          { speaker: "user", text: "I'd like to see a cardiologist this week.", offsetMs: 2600 },
          { speaker: "agent", text: "Sure. Dr. Mehta has slots on Thursday at 11 AM and Friday at 4 PM. Which works for you?", offsetMs: 5200 },
          { speaker: "user", text: "Thursday at 11 works.", offsetMs: 9000 },
          { speaker: "agent", text: "Great — may I have the patient's name?", offsetMs: 11200 },
          { speaker: "user", text: "It's for Anita Rao.", offsetMs: 13800 },
          { speaker: "agent", text: "Booked! Anita's appointment is Thursday 11 AM. I've sent the details and directions to your WhatsApp.", offsetMs: 16000 },
        ],
      },
      {
        kind: "call",
        industry: "Automobile",
        title: "Test-drive booking & lead qualification",
        agentName: "Showroom assistant",
        description:
          "Answer vehicle enquiries, capture the customer's requirements, and book a test drive for the right model.",
        href: AUTOMOBILE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi, thanks for your interest! Which model are you looking at?", offsetMs: 200 },
          { speaker: "user", text: "The top automatic variant — can I test drive it this weekend?", offsetMs: 2600 },
          { speaker: "agent", text: "You can. I have a slot at the Whitefield showroom on Saturday 11 AM or Sunday 4 PM.", offsetMs: 5400 },
          { speaker: "user", text: "Saturday 11 is good.", offsetMs: 9200 },
          { speaker: "agent", text: "Done. Would you like a finance quote ready when you visit?", offsetMs: 11400 },
          { speaker: "user", text: "Yes, please.", offsetMs: 14000 },
          { speaker: "agent", text: "Perfect — I've booked Saturday 11 AM and sent the location and quote to your phone.", offsetMs: 16200 },
        ],
      },
      {
        kind: "call",
        industry: "Real Estate",
        title: "Property enquiry & site-visit booking",
        agentName: "Sales assistant",
        description:
          "Understand a buyer's requirements, answer questions about available properties, and schedule a site visit.",
        href: REALESTATE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi! Thanks for enquiring about the Riverside project. Are you looking for a 2 or 3 BHK?", offsetMs: 200 },
          { speaker: "user", text: "A 3 BHK, ready to move in, around 1.2 crore.", offsetMs: 3000 },
          { speaker: "agent", text: "We have ready-to-move 3 BHKs in that range. Would you like to visit this weekend?", offsetMs: 6000 },
          { speaker: "user", text: "Sunday afternoon works.", offsetMs: 9600 },
          { speaker: "agent", text: "Great — I'll book Sunday 3 PM. Should I arrange a pickup from your area?", offsetMs: 11800 },
          { speaker: "user", text: "Yes, from Hitech City.", offsetMs: 14600 },
          { speaker: "agent", text: "Done. I've shared the floor plan, pricing and directions on WhatsApp. See you Sunday!", offsetMs: 16800 },
        ],
      },
      {
        kind: "whatsapp",
        industry: "Quto",
        title: "Website AI assistant",
        agentName: "Quto Chatbot Agent",
        description:
          "Add your website URL and Quto answers visitor questions using the information already on your site.",
        href: QUTO_HREF,
        linkLabel: QUTO_LINK,
        transcript: [
          { speaker: "user", text: "Do you offer same-day delivery in my area?", offsetMs: 200 },
          { speaker: "agent", text: "Yes — same-day delivery is available within the city on orders placed before 4 PM.", offsetMs: 2600 },
          { speaker: "user", text: "And what's your return window?", offsetMs: 6000 },
          { speaker: "agent", text: "Returns are accepted within 7 days of delivery, with a free pickup.", offsetMs: 8400 },
          { speaker: "user", text: "Great. Are you open on Sundays?", offsetMs: 11400 },
          { speaker: "agent", text: "We're open 10 AM–4 PM on Sundays. Anything else I can help with?", offsetMs: 13600 },
        ],
      },
    ],
  },
  {
    stage: "Retain",
    entries: [
      {
        kind: "call",
        industry: "Healthcare",
        title: "Appointment reminders & follow-up",
        agentName: "Clinic assistant",
        description:
          "Remind patients about upcoming appointments and follow up after a visit so care stays on track.",
        href: HEALTHCARE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi Anita, a quick reminder that your follow-up with Dr. Mehta is tomorrow at 11 AM.", offsetMs: 200 },
          { speaker: "user", text: "Thanks. Do I need to bring my earlier reports?", offsetMs: 3000 },
          { speaker: "agent", text: "Yes, please carry your last ECG and prescription. Shall I confirm the appointment?", offsetMs: 5800 },
          { speaker: "user", text: "Yes, confirmed.", offsetMs: 9400 },
          { speaker: "agent", text: "Great, you're set for tomorrow at 11 AM. Reply here anytime if your plans change.", offsetMs: 11600 },
        ],
      },
      {
        kind: "call",
        industry: "Automobile",
        title: "Service & renewal reminders",
        agentName: "Showroom assistant",
        description:
          "Nudge customers when a service, insurance or PUC renewal is due, and book the slot in one tap.",
        href: AUTOMOBILE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi Rohan, your car's periodic service is due this week, and insurance renews on the 28th.", offsetMs: 200 },
          { speaker: "user", text: "Can you book the service for Saturday?", offsetMs: 3200 },
          { speaker: "agent", text: "Sure — Saturday 10 AM with a free pickup. Shall I add the insurance renewal quote too?", offsetMs: 5800 },
          { speaker: "user", text: "Yes, please.", offsetMs: 9400 },
          { speaker: "agent", text: "Booked for Saturday 10 AM. I've sent the pickup details and your renewal quote to WhatsApp.", offsetMs: 11600 },
        ],
      },
      {
        kind: "call",
        industry: "Real Estate",
        title: "Lead follow-up & visit reminders",
        agentName: "Sales assistant",
        description:
          "Follow up with interested buyers, share updates, and remind them about their scheduled site visit.",
        href: REALESTATE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "agent", text: "Hi Meera, following up on your interest in Riverside — a 3 BHK with a garden view just opened up.", offsetMs: 200 },
          { speaker: "user", text: "Oh nice. Can I see it this weekend?", offsetMs: 3400 },
          { speaker: "agent", text: "Absolutely. Your visit is Saturday 11 AM — shall I add this unit to the same visit?", offsetMs: 6200 },
          { speaker: "user", text: "Yes, add it.", offsetMs: 10000 },
          { speaker: "agent", text: "Done. I've updated your Saturday 11 AM visit and sent the new floor plan to your WhatsApp.", offsetMs: 12200 },
        ],
      },
      {
        kind: "whatsapp",
        industry: "Quto",
        title: "Answers for returning visitors",
        agentName: "Quto Chatbot Agent",
        description:
          "When visitors come back with more questions, Quto answers instantly from your website content.",
        href: QUTO_HREF,
        linkLabel: QUTO_LINK,
        transcript: [
          { speaker: "user", text: "Hi, I was here last week — has the new pricing gone live?", offsetMs: 200 },
          { speaker: "agent", text: "Yes, the updated plans are live. The Starter plan is now ₹999/month.", offsetMs: 2800 },
          { speaker: "user", text: "Does that include priority support?", offsetMs: 6200 },
          { speaker: "agent", text: "Priority support is included on the Growth plan and above.", offsetMs: 8600 },
          { speaker: "user", text: "Got it, thanks.", offsetMs: 11600 },
          { speaker: "agent", text: "Happy to help — I'm here whenever you have more questions.", offsetMs: 13400 },
        ],
      },
    ],
  },
  {
    stage: "Support",
    entries: [
      {
        kind: "call",
        industry: "Healthcare",
        title: "Patient enquiry support",
        agentName: "Clinic assistant",
        description:
          "Answer common questions about timings, services and reports, and point patients to the right next step.",
        href: HEALTHCARE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "user", text: "Hi, what are your OPD timings on weekends?", offsetMs: 200 },
          { speaker: "agent", text: "Our OPD is open 9 AM–1 PM on Saturdays and closed on Sundays.", offsetMs: 2600 },
          { speaker: "user", text: "Can I get a copy of my blood test report?", offsetMs: 6000 },
          { speaker: "agent", text: "Yes — reports are on our patient portal, or I can email them to your registered address.", offsetMs: 8400 },
          { speaker: "user", text: "Please email them.", offsetMs: 12200 },
          { speaker: "agent", text: "Done, I've sent them to your registered email. Anything else I can help with?", offsetMs: 14200 },
        ],
      },
      {
        kind: "call",
        industry: "Automobile",
        title: "Service & vehicle support",
        agentName: "Showroom assistant",
        description:
          "Handle service-status and vehicle questions, and route anything complex to your team.",
        href: AUTOMOBILE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "user", text: "Hi, is my car ready for pickup after the service?", offsetMs: 200 },
          { speaker: "agent", text: "Let me check — yes, the service is complete and it's ready for pickup after 5 PM today.", offsetMs: 2800 },
          { speaker: "user", text: "Great. Were the front brake pads replaced?", offsetMs: 6600 },
          { speaker: "agent", text: "Yes, the front brake pads were replaced and it's noted on your invoice.", offsetMs: 9000 },
          { speaker: "user", text: "Perfect, thanks.", offsetMs: 12000 },
          { speaker: "agent", text: "You're welcome. I've sent the invoice and pickup details to your phone.", offsetMs: 13800 },
        ],
      },
      {
        kind: "call",
        industry: "Real Estate",
        title: "Project & property support",
        agentName: "Sales assistant",
        description:
          "Answer questions about pricing, amenities and availability using your project information.",
        href: REALESTATE_HREF,
        linkLabel: INDUSTRY_LINK,
        transcript: [
          { speaker: "user", text: "Hi, does the Riverside project have covered parking?", offsetMs: 200 },
          { speaker: "agent", text: "Yes — every 3 BHK comes with two covered parking spaces.", offsetMs: 2600 },
          { speaker: "user", text: "And is there a clubhouse?", offsetMs: 5800 },
          { speaker: "agent", text: "There's a clubhouse with a gym and a pool, included in the amenities.", offsetMs: 8000 },
          { speaker: "user", text: "What's the maintenance cost?", offsetMs: 11000 },
          { speaker: "agent", text: "Maintenance is ₹3.5 per sq ft a month. I can share the full amenities brochure if you'd like.", offsetMs: 13200 },
        ],
      },
      {
        kind: "whatsapp",
        industry: "Quto",
        title: "Instant answers from your site",
        agentName: "Quto Chatbot Agent",
        description:
          "Visitors get a direct answer instead of searching your site — Quto replies from what's already published.",
        href: QUTO_HREF,
        linkLabel: QUTO_LINK,
        transcript: [
          { speaker: "user", text: "Where can I find your refund policy?", offsetMs: 200 },
          { speaker: "agent", text: "No need to search — refunds are processed within 5–7 business days to the original payment method.", offsetMs: 2600 },
          { speaker: "user", text: "Is there a cancellation fee?", offsetMs: 6400 },
          { speaker: "agent", text: "No cancellation fee if you cancel within 24 hours of ordering.", offsetMs: 8800 },
          { speaker: "user", text: "Thanks, that's exactly what I needed.", offsetMs: 12000 },
          { speaker: "agent", text: "Glad I could help — all answered right here from your site.", offsetMs: 13800 },
        ],
      },
    ],
  },
];
